/**
 * Shared, isomorphic contract for the partner booking-origin cookie.
 *
 * The /cart and /booking routes live OUTSIDE the /partner/<slug>/ route group,
 * so the partner-aware search forms persist the active storefront here whenever
 * a search runs. Both the client (PartnerContext, via document.cookie) and the
 * server (JourneyChrome, via next/headers cookies()) read/write through this one
 * module, so the cookie's name and shape live in a single place. That single
 * source of truth is also what makes a future standalone partner app a clean
 * lift-out — nothing else needs to know how the origin is encoded.
 *
 * Shape: URL-encoded JSON { id, slug }.
 *  - `id`   keeps attribution parity with the original cookie (presence => the
 *           checkout started from a partner storefront => bookingPlatform = 10).
 *  - `slug` lets the shared routes resolve the partner profile server-side so the
 *           whole journey (cart/booking) can render the partner's branded chrome.
 *
 * Legacy plain-id cookie values (written before this change) are tolerated so an
 * in-flight session created under the old format keeps working.
 */

export const PARTNER_ORIGIN_COOKIE = "sukuto_partner_origin";
export const PARTNER_ORIGIN_MAX_AGE = 6 * 60 * 60; // 6h — one booking session

/**
 * Encode a partner profile into the cookie value. Returns "" when there is
 * nothing worth persisting (so callers clear the cookie instead).
 *
 * @param {{id?: string|number, slug?: string}|null|undefined} partner
 * @returns {string}
 */
export function encodePartnerOrigin(partner) {
  const id = partner?.id ?? null;
  const slug = partner?.slug ?? null;
  if (!id && !slug) return "";
  return encodeURIComponent(JSON.stringify({ id, slug }));
}

/**
 * Parse a raw cookie value (from document.cookie or next/headers) into
 * { id, slug }, or null when absent/unparseable. Tolerant of the legacy
 * plain-id format.
 *
 * @param {string|null|undefined} rawValue
 * @returns {{id: (string|number|null), slug: (string|null)}|null}
 */
export function parsePartnerOrigin(rawValue) {
  if (!rawValue) return null;

  let value = rawValue;
  try {
    value = decodeURIComponent(rawValue);
  } catch {
    // Value was not URL-encoded; use it as-is.
  }

  try {
    const obj = JSON.parse(value);
    if (obj && (obj.id || obj.slug)) {
      return { id: obj.id ?? null, slug: obj.slug ?? null };
    }
    return null;
  } catch {
    // Legacy format: the cookie held a bare partner id string.
    return { id: value, slug: null };
  }
}
